import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Stack,
  Divider,
  Avatar
} from '@mui/material';
import {
  Event as EventIcon,
  LocationOn,
  AccessTime,
  AttachMoney,
  People,
  CalendarToday,
} from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAuth } from '../../context/AuthContext';
import { eventAPI, memberProfileAPI } from '../../services/api';
import { Event, EventStatus, RegistrationStatus, EventRegistration, MemberProfile } from '../../types';
import { blue } from '@mui/material/colors';

const EventsPage: React.FC = () => {
  const { user, selectedMemberProfile: currentMemberProfile } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [userRegistrations, setUserRegistrations] = useState<EventRegistration[]>([]);
  const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Registration dialog state
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedMemberProfile, setSelectedMemberProfile] = useState<string>('');
  const [registering, setRegistering] = useState<boolean>(false);
  const [guests, setGuests] = useState<{ name: string; email: string; phone: string }[]>([]);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Unregister dialog state
  const [unregisterDialogOpen, setUnregisterDialogOpen] = useState<boolean>(false);
  const [selectedRegistration, setSelectedRegistration] = useState<EventRegistration | null>(null);
  const [unregistering, setUnregistering] = useState<boolean>(false);

  // Fetch events and user registrations
  const fetchData = async () => {
    setLoading(true);
    try {
      // Get published events only
      const eventsResponse = await eventAPI.getAll({ 
        status: EventStatus.PUBLISHED, 
        upcoming: true 
      });
      setEvents(eventsResponse.data);

      // Get user's registrations if logged in
      if (user) {
        try {
          const registrationsResponse = await eventAPI.getUserRegistrations(user.id);
          setUserRegistrations(registrationsResponse.data);
        } catch (regError) {
          console.error('Error fetching user registrations:', regError);
          // Don't set error state for this, as it's not critical
        }

        // Get user's member profiles
        try {
          const profilesResponse = await memberProfileAPI.getUserMemberProfiles(user.id);
          console.log('Member profiles response:', profilesResponse.data);
          setMemberProfiles(profilesResponse.data || []);
        } catch (profileError) {
          console.error('Error fetching member profiles:', profileError);
        }
      }

      setError(null);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Check if the currently selected member profile is registered for an event
  const isUserRegistered = (eventId: string): EventRegistration | null => {
    if (!currentMemberProfile) return null;
    
    return userRegistrations.find(reg => 
      reg.eventId === eventId && 
      reg.memberProfileId === currentMemberProfile.id &&
      (reg.status === RegistrationStatus.REGISTERED || reg.status === RegistrationStatus.WAITLISTED)
    ) || null;
  };

  // Handle registration
  const handleRegister = async () => {
    if (!selectedEvent || !selectedMemberProfile) return;

    setRegistering(true);
    setDialogError(null); // Clear any previous dialog errors
    try {
      const response = await eventAPI.register(selectedEvent.id, {
        memberProfileId: selectedMemberProfile,
        guests: guests.length > 0 ? guests : undefined
      });

      setSuccess(response.data.message);
      setRegistrationDialogOpen(false);
      setSelectedEvent(null);
      setSelectedMemberProfile('');
      setGuests([]);
      setDialogError(null);
      
      // Refresh data
      await fetchData();
    } catch (error: any) {
      // Show error in dialog instead of page
      setDialogError(error.response?.data?.message || 'Failed to register for event');
    } finally {
      setRegistering(false);
    }
  };

  // Handle unregistration
  const handleUnregister = async () => {
    if (!selectedRegistration) return;

    setUnregistering(true);
    try {
      const response = await eventAPI.cancelRegistration(selectedRegistration.id, 'User cancelled registration');
      
      // Display message with guest cancellation info if applicable
      const message = response.data.cancelledGuestsCount && response.data.cancelledGuestsCount > 0
        ? `Registration cancelled successfully. ${response.data.cancelledGuestsCount} guest registration(s) were also cancelled.`
        : response.data.message || 'Registration cancelled successfully';
      
      setSuccess(message);
      setUnregisterDialogOpen(false);
      setSelectedRegistration(null);
      
      // Refresh data
      await fetchData();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to cancel registration');
    } finally {
      setUnregistering(false);
    }
  };

  // Open unregister dialog
  const openUnregisterDialog = (registration: EventRegistration) => {
    setSelectedRegistration(registration);
    setUnregisterDialogOpen(true);
  };

  // Close unregister dialog
  const closeUnregisterDialog = () => {
    setUnregisterDialogOpen(false);
    setSelectedRegistration(null);
  };

  // Open registration dialog
  const openRegistrationDialog = (event: Event) => {
    setSelectedEvent(event);
    setRegistrationDialogOpen(true);
  };

  // Close registration dialog
  const closeRegistrationDialog = () => {
    setRegistrationDialogOpen(false);
    setSelectedEvent(null);
    setSelectedMemberProfile('');
    setGuests([]);
    setDialogError(null); // Clear dialog error when closing
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <RouterLink to="/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </RouterLink>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <EventIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Events
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Upcoming Events
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Discover and register for exciting events happening at our club
        </Typography>
      </Paper>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {user && !currentMemberProfile && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please select a member profile from the dashboard to see your event registrations and register for events.
        </Alert>
      )}

      {/* Events Grid */}
      {events.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No upcoming events
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for new events and activities
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {events.map((event) => {
            const userRegistration = isUserRegistered(event.id);
            const isRegistered = !!userRegistration;
            const isOnWaitlist = userRegistration?.status === RegistrationStatus.WAITLISTED;
            const spotsLeft = event.maxRegistrants ? (event.spotsRemaining ?? 0) : null;
            const isFull = event.maxRegistrants && (event.spotsRemaining ?? 0) <= 0;
            
            // Check if registration is open (backend handles all date logic)
            const registrationClosed = event.isRegistrationOpen === false;
            
            // Count guest registrations for this event for the current member profile
            const guestRegistrations = userRegistrations.filter(reg => 
              reg.eventId === event.id && 
              reg.isGuest && 
              currentMemberProfile && 
              reg.memberProfileId === currentMemberProfile.id
            );

            return (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={event.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    {/* Category */}
                    {event.category && (
                      <Chip 
                        label={event.category} 
                        size="small" 
                        sx={{ mb: 1 }}
                        color="primary"
                        variant="outlined"
                      />
                    )}

                    {/* Title */}
                    <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {event.title}
                    </Typography>

                    {/* Description */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {event.description.length > 100 
                        ? `${event.description.substring(0, 100)}...`
                        : event.description
                      }
                    </Typography>

                    {/* Event Details */}
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(event.eventDateTime)}
                        </Typography>
                      </Box>

                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.location}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachMoney sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {event.cost > 0 ? formatCurrency(event.cost) : 'Free'}
                        </Typography>
                      </Box>

                      {event.maxRegistrants && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <People sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {event.registeredCount || 0} / {event.maxRegistrants} registered
                            {spotsLeft !== null && (
                              <span style={{ fontWeight: 'bold', color: spotsLeft <= 5 ? '#d32f2f' : 'inherit' }}>
                                {spotsLeft > 0 ? ` (${spotsLeft} spots left)` : ' (Full)'}
                              </span>
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Registration Status */}
                    {isRegistered && (
                      <Box>
                        <Chip
                          label={isOnWaitlist ? `Waitlisted (#${userRegistration?.waitlistPosition})` : 'Registered'}
                          color={isOnWaitlist ? 'warning' : 'success'}
                          sx={{ mb: 1 }}
                        />
                        {guestRegistrations.length > 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            + {guestRegistrations.length} guest{guestRegistrations.length > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    {!user ? (
                      <Button
                        component={RouterLink}
                        to="/login"
                        variant="contained"
                        fullWidth
                      >
                        Login to Register
                      </Button>
                    ) : isRegistered ? (
                      <Stack spacing={1} sx={{ width: '100%' }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          color="error"
                          onClick={() => openUnregisterDialog(userRegistration!)}
                        >
                          Unregister
                        </Button>
                        {guestRegistrations.length > 0 && (
                          <Box sx={{ pt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Guest Registrations:
                            </Typography>
                            {guestRegistrations.map((guestReg, idx) => (
                              <Box key={guestReg.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                  {guestReg.guestName}
                                </Typography>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => openUnregisterDialog(guestReg)}
                                  sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Stack>
                    ) : isFull && !event.waitlistEnabled ? (
                      <Button
                        variant="outlined"
                        fullWidth
                        disabled
                      >
                        Event Full
                      </Button>
                    ) : registrationClosed ? (
                      <Button
                        variant="outlined"
                        fullWidth
                        disabled
                      >
                        {event.registrationStartDate && new Date(event.registrationStartDate) > new Date()
                          ? `Registration Opens ${new Date(event.registrationStartDate).toLocaleDateString()}`
                          : 'Registration Closed'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => openRegistrationDialog(event)}
                      >
                        {isFull ? 'Join Waitlist' : 'Register'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Registration Dialog */}
      <Dialog 
        open={registrationDialogOpen}
        disableEscapeKeyDown={true}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Register for {selectedEvent?.title}
        </DialogTitle>
        <DialogContent>
          {/* Show error in dialog */}
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}

          <DialogContentText sx={{ mb: 3 }}>
            {selectedEvent?.cost && selectedEvent.cost > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This event costs {formatCurrency(selectedEvent.cost)}. 
                You will be charged upon registration.
              </Alert>
            )}

            Choose which member profile to register with:
          </DialogContentText>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Member Profile</InputLabel>
            <Select
              value={selectedMemberProfile}
              onChange={(e) => setSelectedMemberProfile(e.target.value)}
              label="Member Profile"
            >
              {memberProfiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.firstName} {profile.lastName} ({profile.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Guest Registration Section */}
          {selectedEvent?.guestsAllowed && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Guest Registration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedEvent.guestFee && selectedEvent.guestFee > 0
                  ? `Guest fee: ${formatCurrency(selectedEvent.guestFee)} per guest`
                  : 'Guests can attend for free'}
                {selectedEvent.maxGuestsPerMember && 
                  ` • Max ${selectedEvent.maxGuestsPerMember} guest(s) per member`}
              </Typography>

              {guests.map((guest, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Guest {index + 1}</Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        const newGuests = guests.filter((_, i) => i !== index);
                        setGuests(newGuests);
                      }}
                    >
                      Remove
                    </Button>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Guest Name"
                        required
                        value={guest.name}
                        onChange={(e) => {
                          const newGuests = [...guests];
                          newGuests[index].name = e.target.value;
                          setGuests(newGuests);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Guest Email"
                        type="email"
                        required
                        value={guest.email}
                        onChange={(e) => {
                          const newGuests = [...guests];
                          newGuests[index].email = e.target.value;
                          setGuests(newGuests);
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Guest Phone"
                        type="tel"
                        required
                        value={guest.phone}
                        onChange={(e) => {
                          const newGuests = [...guests];
                          newGuests[index].phone = e.target.value;
                          setGuests(newGuests);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}

              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  if (selectedEvent.maxGuestsPerMember && guests.length >= selectedEvent.maxGuestsPerMember) {
                    setError(`Maximum ${selectedEvent.maxGuestsPerMember} guest(s) allowed per member`);
                    return;
                  }
                  setGuests([...guests, { name: '', email: '', phone: '' }]);
                }}
                disabled={
                  selectedEvent.maxGuestsPerMember 
                    ? guests.length >= selectedEvent.maxGuestsPerMember 
                    : false
                }
              >
                Add Guest
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRegistrationDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleRegister}
            variant="contained"
            disabled={!selectedMemberProfile || registering}
          >
            {registering ? <CircularProgress size={20} /> : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unregister Confirmation Dialog */}
      <Dialog 
        open={unregisterDialogOpen}
        onClose={closeUnregisterDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle color="error">
          {selectedRegistration?.isGuest ? 'Cancel Guest Registration' : 'Cancel Registration'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Warning:</strong> This action is irreversible.
            </Alert>
            
            {selectedRegistration?.isGuest ? (
              <>
                Are you sure you want to cancel the guest registration for <strong>{selectedRegistration.guestName}</strong>?
                {selectedRegistration.amountPaid > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <strong>Note:</strong> A fee of {formatCurrency(selectedRegistration.amountPaid)} was charged for this guest.
                  </Box>
                )}
              </>
            ) : (
              <>
                Are you sure you want to cancel your registration for this event? 
                Once cancelled, you will need to register again if you change your mind, 
                and your spot may no longer be available.
                
                {(() => {
                  // Count guests for this registration
                  const guestCount = selectedRegistration 
                    ? userRegistrations.filter(reg => 
                        reg.eventId === selectedRegistration.eventId && 
                        reg.isGuest && 
                        reg.parentMemberUserId === user?.id
                      ).length
                    : 0;
                  
                  return guestCount > 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <strong>Important:</strong> Cancelling your registration will also automatically cancel {guestCount} guest registration{guestCount > 1 ? 's' : ''} associated with your attendance. Guests cannot attend without a member present.
                    </Alert>
                  ) : null;
                })()}
              </>
            )}
            
            {selectedRegistration?.status === RegistrationStatus.WAITLISTED && (
              <Box sx={{ mt: 2 }}>
                <strong>Note:</strong> You are currently on the waitlist for this event.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUnregisterDialog}>
            Keep Registration
          </Button>
          <Button 
            onClick={handleUnregister}
            variant="contained"
            color="error"
            disabled={unregistering}
          >
            {unregistering ? <CircularProgress size={20} /> : 'Cancel Registration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventsPage;
